import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { httpGet, httpPut, httpPostFormData } from '../services/https';
import Layout from '../components/Layout';

const ODFPage = () => {
  const [activeTab, setActiveTab] = useState('summary'); // 'summary' or 'communities'

  // Summary State
  const [lgaSummaries, setLgaSummaries] = useState([]);
  const [loadingSummary, setLoadingSummary] = useState(true);

  // Communities Directory State
  const [communities, setCommunities] = useState([]);
  const [loadingCommunities, setLoadingCommunities] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCommunities, setTotalCommunities] = useState(0);

  // Filters & Search
  const [search, setSearch] = useState('');
  const [selectedLga, setSelectedLga] = useState('All');
  const [selectedOdfStatus, setSelectedOdfStatus] = useState('All');
  const [selectedTriggered, setSelectedTriggered] = useState('All');

  // Edit Community Modal
  const [editingCommunity, setEditingCommunity] = useState(null);
  const [editForm, setEditForm] = useState({
    odf_certified: 'NO',
    odf_certified_date: '',
    odf_claiming: 'NO',
    is_triggered: false,
    date_triggered: '',
    improved_hh_latrines: 0,
    unimproved_hh_latrines: 0,
    population: 0,
    leader_name: '',
    leader_phone: ''
  });
  const [savingEdit, setSavingEdit] = useState(false);

  // Excel Upload Modal
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState(null);

  // Fetch LGA Summaries
  const fetchSummaries = async () => {
    setLoadingSummary(true);
    try {
      const response = await httpGet('/odf/cds/summary');
      if (response && response.status && response.data) {
        setLgaSummaries(response.data);
      } else {
        // Fallback to legacy GET /odf
        const legacyRes = await httpGet('/odf/odf');
        if (Array.isArray(legacyRes)) {
          setLgaSummaries(legacyRes);
        }
      }
    } catch (error) {
      console.error('Error fetching ODF summary:', error);
    } finally {
      setLoadingSummary(false);
    }
  };

  // Fetch Communities Directory
  const fetchCommunities = async () => {
    setLoadingCommunities(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: 50,
        search: search.trim(),
        lga: selectedLga,
        odf_certified: selectedOdfStatus,
        triggered: selectedTriggered
      });
      const response = await httpGet(`/odf/cds/communities?${params.toString()}`);
      if (response && response.status) {
        setCommunities(response.data || []);
        setTotalPages(response.totalPages || 1);
        setTotalCommunities(response.total || 0);
      }
    } catch (error) {
      console.error('Error fetching CDS communities:', error);
    } finally {
      setLoadingCommunities(false);
    }
  };

  useEffect(() => {
    fetchSummaries();
  }, []);

  useEffect(() => {
    if (activeTab === 'communities') {
      fetchCommunities();
    }
  }, [activeTab, currentPage, selectedLga, selectedOdfStatus, selectedTriggered]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchCommunities();
  };

  // Open Edit Modal
  const handleEditClick = (comm) => {
    setEditingCommunity(comm);
    setEditForm({
      odf_certified: comm.odf_certified || 'NO',
      odf_certified_date: comm.odf_certified_date || '',
      odf_claiming: comm.odf_claiming || 'NO',
      is_triggered: comm.is_triggered || false,
      date_triggered: comm.date_triggered || '',
      improved_hh_latrines: comm.improved_hh_latrines || 0,
      unimproved_hh_latrines: comm.unimproved_hh_latrines || 0,
      population: comm.population || 0,
      leader_name: comm.leader_name || '',
      leader_phone: comm.leader_phone || ''
    });
  };

  // Save Edit Community
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingCommunity) return;
    setSavingEdit(true);
    try {
      const res = await httpPut(`/odf/cds/community/${editingCommunity.id}`, editForm);
      if (res && res.status) {
        setEditingCommunity(null);
        fetchCommunities();
        fetchSummaries();
      } else {
        alert('Failed to update community data');
      }
    } catch (err) {
      console.error('Error updating community:', err);
      alert('Error updating community data');
    } finally {
      setSavingEdit(false);
    }
  };

  // File Upload Handler
  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!uploadFile) {
      alert('Please select a CDS Excel (.xlsx) file first');
      return;
    }
    setUploading(true);
    setUploadMessage(null);

    const formData = new FormData();
    formData.append('file', uploadFile);

    try {
      const res = await httpPostFormData('/odf/cds/upload', formData);
      if (res && res.status) {
        setUploadMessage({ type: 'success', text: res.message || 'Excel file imported successfully!' });
        fetchSummaries();
        if (activeTab === 'communities') fetchCommunities();
        setTimeout(() => {
          setShowUploadModal(false);
          setUploadFile(null);
          setUploadMessage(null);
        }, 2000);
      } else {
        setUploadMessage({ type: 'error', text: res?.message || 'Failed to upload CDS file' });
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadMessage({ type: 'error', text: 'Error uploading file. Please try again.' });
    } finally {
      setUploading(false);
    }
  };

  // Calculate global summary stats
  const totalComms = lgaSummaries.reduce((sum, item) => sum + (parseInt(item.no_of_communities) || 0), 0);
  const totalTriggered = lgaSummaries.reduce((sum, item) => sum + (parseInt(item.no_of_triggered) || 0), 0);
  const totalCertified = lgaSummaries.reduce((sum, item) => sum + (parseInt(item.no_of_certified) || 0), 0);
  const overallCertifiedPct = totalComms > 0 ? Math.round((totalCertified / totalComms) * 100) : 0;

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto space-y-6">
        {/* Top Header & Actions */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">SURWASH ODF & CDS Dashboard</h1>
            <p className="text-sm text-gray-500 mt-1">
              Community Distribution Status (CDS) and Open Defecation Free (ODF) tracking across Kaduna LGAs
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/odf"
              className="inline-flex items-center px-4 py-2 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 font-medium rounded-lg transition text-sm"
            >
              🗺️ View Interactive Map
            </Link>
            <Link
              to="/odf/add"
              className="inline-flex items-center px-4 py-2 bg-blue-50 text-blue-700 hover:bg-blue-100 font-medium rounded-lg transition text-sm"
            >
              📤 Upload Data Page
            </Link>
            <button
              onClick={() => setShowUploadModal(true)}
              className="inline-flex items-center px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg shadow-sm transition text-sm"
            >
              📥 Quick Upload Excel
            </button>
          </div>
        </div>

        {/* Global Key Metrics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Total Communities</p>
              <h3 className="text-2xl font-bold text-gray-800 mt-1">{totalComms.toLocaleString()}</h3>
            </div>
            <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center text-xl font-bold">
              🏘️
            </div>
          </div>

          <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Triggered Communities</p>
              <h3 className="text-2xl font-bold text-amber-600 mt-1">{totalTriggered.toLocaleString()}</h3>
            </div>
            <div className="w-12 h-12 bg-amber-50 text-amber-600 rounded-xl flex items-center justify-center text-xl font-bold">
              🔥
            </div>
          </div>

          <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">ODF Certified</p>
              <h3 className="text-2xl font-bold text-emerald-600 mt-1">{totalCertified.toLocaleString()}</h3>
            </div>
            <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center text-xl font-bold">
              ✅
            </div>
          </div>

          <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Certification Rate</p>
              <h3 className="text-2xl font-bold text-indigo-600 mt-1">{overallCertifiedPct}%</h3>
            </div>
            <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center text-xl font-bold">
              📊
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 flex space-x-8">
          <button
            onClick={() => setActiveTab('summary')}
            className={`pb-3 font-semibold text-sm transition ${
              activeTab === 'summary'
                ? 'border-b-2 border-emerald-600 text-emerald-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            LGA Overview & Aggregates
          </button>
          <button
            onClick={() => setActiveTab('communities')}
            className={`pb-3 font-semibold text-sm transition ${
              activeTab === 'communities'
                ? 'border-b-2 border-emerald-600 text-emerald-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Community Directory (CDS Dataset)
          </button>
        </div>

        {/* TAB 1: LGA Summary Table */}
        {activeTab === 'summary' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            {loadingSummary ? (
              <div className="p-8 text-center text-gray-500">Loading LGA ODF statistics...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        LGA Name
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Total Communities
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Triggered Communities
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        ODF Certified
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Progress Rate (%)
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Total Population
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {lgaSummaries.map((item, idx) => {
                      const comms = parseInt(item.no_of_communities) || 0;
                      const cert = parseInt(item.no_of_certified) || 0;
                      const trig = parseInt(item.no_of_triggered) || 0;
                      const pop = parseInt(item.total_population) || 0;
                      const pct = comms > 0 ? Math.round((cert / comms) * 100) : 0;

                      return (
                        <tr key={idx} className="hover:bg-gray-50 transition">
                          <td className="px-6 py-4 whitespace-nowrap font-bold text-gray-800">
                            {item.lga}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center text-gray-700 font-medium">
                            {comms.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center text-amber-700 font-medium">
                            {trig.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center text-emerald-700 font-bold">
                            {cert.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-24 bg-gray-200 rounded-full h-2.5 overflow-hidden">
                                <div
                                  className={`h-2.5 rounded-full ${
                                    pct >= 70 ? 'bg-emerald-500' : pct >= 40 ? 'bg-amber-500' : 'bg-rose-500'
                                  }`}
                                  style={{ width: `${Math.min(pct, 100)}%` }}
                                ></div>
                              </div>
                              <span className="text-xs font-semibold text-gray-600">{pct}%</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-gray-600">
                            {pop > 0 ? pop.toLocaleString() : 'N/A'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* TAB 2: Communities Directory */}
        {activeTab === 'communities' && (
          <div className="space-y-4">
            {/* Search and Filters Bar */}
            <form onSubmit={handleSearchSubmit} className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex flex-col md:flex-row gap-4 items-center justify-between">
              <div className="flex-1 w-full flex items-center gap-3">
                <input
                  type="text"
                  placeholder="Search community name, ward, or code..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                />
                <button
                  type="submit"
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-sm rounded-lg transition"
                >
                  Search
                </button>
              </div>

              <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
                <select
                  value={selectedLga}
                  onChange={(e) => { setSelectedLga(e.target.value); setCurrentPage(1); }}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white"
                >
                  <option value="All">All LGAs</option>
                  {lgaSummaries.map((s, i) => (
                    <option key={i} value={s.lga}>{s.lga}</option>
                  ))}
                </select>

                <select
                  value={selectedOdfStatus}
                  onChange={(e) => { setSelectedOdfStatus(e.target.value); setCurrentPage(1); }}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white"
                >
                  <option value="All">All ODF Statuses</option>
                  <option value="YES">Certified ODF (YES)</option>
                  <option value="NO">Not Certified (NO)</option>
                </select>

                <select
                  value={selectedTriggered}
                  onChange={(e) => { setSelectedTriggered(e.target.value); setCurrentPage(1); }}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white"
                >
                  <option value="All">All Triggered Statuses</option>
                  <option value="true">Triggered Only</option>
                  <option value="false">Not Triggered</option>
                </select>
              </div>
            </form>

            {/* Communities Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              {loadingCommunities ? (
                <div className="p-8 text-center text-gray-500">Loading CDS community records...</div>
              ) : communities.length === 0 ? (
                <div className="p-8 text-center text-gray-500">No community records found matching criteria.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-gray-500">Community Name</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-500">Ward</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-500">LGA</th>
                        <th className="px-4 py-3 text-center font-semibold text-gray-500">Triggered</th>
                        <th className="px-4 py-3 text-center font-semibold text-gray-500">ODF Status</th>
                        <th className="px-4 py-3 text-center font-semibold text-gray-500">Improved Latrines</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-500">Leader Phone</th>
                        <th className="px-4 py-3 text-right font-semibold text-gray-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {communities.map((comm) => (
                        <tr key={comm.id} className="hover:bg-gray-50 transition">
                          <td className="px-4 py-3 font-semibold text-gray-800">
                            {comm.community_name}
                            {comm.community_code && (
                              <span className="block text-xs text-gray-400 font-normal">{comm.community_code}</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-600">{comm.ward}</td>
                          <td className="px-4 py-3 text-gray-700 font-medium">{comm.lga}</td>
                          <td className="px-4 py-3 text-center">
                            {comm.is_triggered ? (
                              <span className="px-2.5 py-1 bg-amber-100 text-amber-800 text-xs font-semibold rounded-full">
                                Triggered
                              </span>
                            ) : (
                              <span className="px-2.5 py-1 bg-gray-100 text-gray-500 text-xs font-medium rounded-full">
                                No
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {String(comm.odf_certified).toUpperCase() === 'YES' ? (
                              <span className="px-2.5 py-1 bg-emerald-100 text-emerald-800 text-xs font-bold rounded-full">
                                Certified ODF
                              </span>
                            ) : String(comm.odf_claiming).toUpperCase() === 'YES' ? (
                              <span className="px-2.5 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">
                                Claimed ODF
                              </span>
                            ) : (
                              <span className="px-2.5 py-1 bg-rose-100 text-rose-700 text-xs font-medium rounded-full">
                                Not Certified
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center font-medium text-gray-700">
                            {comm.improved_hh_latrines || 0}
                          </td>
                          <td className="px-4 py-3 text-gray-500 text-xs">
                            {comm.leader_phone || 'N/A'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => handleEditClick(comm)}
                              className="px-3 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 text-xs font-semibold rounded-md transition"
                            >
                              Edit
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination Controls */}
              <div className="p-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                <span className="text-xs text-gray-500">
                  Showing page {currentPage} of {totalPages} ({totalCommunities} total communities)
                </span>
                <div className="flex gap-2">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    className="px-3 py-1 bg-white border border-gray-300 rounded text-xs font-medium text-gray-700 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    disabled={currentPage >= totalPages}
                    onClick={() => setCurrentPage((p) => p + 1)}
                    className="px-3 py-1 bg-white border border-gray-300 rounded text-xs font-medium text-gray-700 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* MODAL: EDIT COMMUNITY DATA */}
        {editingCommunity && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 space-y-4">
              <div className="flex items-center justify-between border-b pb-3">
                <h3 className="text-lg font-bold text-gray-800">
                  Edit Community: {editingCommunity.community_name}
                </h3>
                <button
                  onClick={() => setEditingCommunity(null)}
                  className="text-gray-400 hover:text-gray-600 font-bold"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleSaveEdit} className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">ODF Certified Status</label>
                    <select
                      value={editForm.odf_certified}
                      onChange={(e) => setEditForm({ ...editForm, odf_certified: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded-lg"
                    >
                      <option value="YES">Certified (YES)</option>
                      <option value="NO">Not Certified (NO)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Certified Date</label>
                    <input
                      type="text"
                      placeholder="e.g. 2024-05"
                      value={editForm.odf_certified_date}
                      onChange={(e) => setEditForm({ ...editForm, odf_certified_date: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Triggered Status</label>
                    <select
                      value={editForm.is_triggered ? 'true' : 'false'}
                      onChange={(e) => setEditForm({ ...editForm, is_triggered: e.target.value === 'true' })}
                      className="w-full p-2 border border-gray-300 rounded-lg"
                    >
                      <option value="true">Triggered (YES)</option>
                      <option value="false">Not Triggered (NO)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Triggered Date</label>
                    <input
                      type="text"
                      placeholder="e.g. 2023-11"
                      value={editForm.date_triggered}
                      onChange={(e) => setEditForm({ ...editForm, date_triggered: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Improved Household Latrines</label>
                    <input
                      type="number"
                      value={editForm.improved_hh_latrines}
                      onChange={(e) => setEditForm({ ...editForm, improved_hh_latrines: parseInt(e.target.value) || 0 })}
                      className="w-full p-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Population</label>
                    <input
                      type="number"
                      value={editForm.population}
                      onChange={(e) => setEditForm({ ...editForm, population: parseInt(e.target.value) || 0 })}
                      className="w-full p-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Leader Name</label>
                    <input
                      type="text"
                      value={editForm.leader_name}
                      onChange={(e) => setEditForm({ ...editForm, leader_name: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Leader Phone</label>
                    <input
                      type="text"
                      value={editForm.leader_phone}
                      onChange={(e) => setEditForm({ ...editForm, leader_phone: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setEditingCommunity(null)}
                    className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={savingEdit}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium"
                  >
                    {savingEdit ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* MODAL: UPLOAD EXCEL FILE */}
        {showUploadModal && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
              <div className="flex items-center justify-between border-b pb-3">
                <h3 className="text-lg font-bold text-gray-800">Upload CDS Excel File (.xlsx)</h3>
                <button
                  onClick={() => setShowUploadModal(false)}
                  className="text-gray-400 hover:text-gray-600 font-bold"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleUploadSubmit} className="space-y-4">
                <p className="text-xs text-gray-500">
                  Select an updated SURWASH CDS spreadsheet file. The backend will update existing community metrics and add new communities automatically.
                </p>

                <input
                  type="file"
                  accept=".xlsx, .xls"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  className="w-full p-2 border border-gray-300 rounded-lg text-xs"
                />

                {uploadMessage && (
                  <div
                    className={`p-3 rounded-lg text-xs font-medium ${
                      uploadMessage.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
                    }`}
                  >
                    {uploadMessage.text}
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-3 border-t">
                  <button
                    type="button"
                    onClick={() => setShowUploadModal(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg text-xs"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={uploading}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold"
                  >
                    {uploading ? 'Processing...' : 'Upload & Import'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ODFPage;